import {
  Placement,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
} from "@floating-ui/react";
import { useMemo, useState } from "react";

export function useTooltip({
  placement = "top",
  delay,
}: {
  placement?: Placement;
  delay?: number;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);

  const data = useFloating({
    placement,
    open: uncontrolledOpen,
    onOpenChange: setUncontrolledOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        crossAxis: placement.includes("-"),
        fallbackAxisSideDirection: "start",
      }),
      shift({ padding: 5 }),
    ],
  });

  const context = data.context;

  const hover = useHover(context, {
    move: false,
    delay,
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });
  const click = useClick(context);

  const interactions = useInteractions([hover, focus, dismiss, role, click]);

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 200,
  });

  return useMemo(
    () => ({
      uncontrolledOpen,
      setUncontrolledOpen,
      isMounted,
      transitionStyles,
      ...interactions,
      ...data,
    }),
    [
      uncontrolledOpen,
      setUncontrolledOpen,
      interactions,
      data,
      isMounted,
      transitionStyles,
    ],
  );
}
